import { useState } from 'react';
import { projects, categories, Project } from '../../../mocks/projekte';
import ProjectModal from './ProjectModal';

const categoryColors: Record<string, string> = {
  'Sanierung & Renovierung': 'bg-amber-100 text-amber-800',
  'Garten- & Landschaftsbau': 'bg-green-100 text-green-800',
  'Trockenbau': 'bg-blue-100 text-blue-800',
  'Fassade & Außenwände': 'bg-stone-100 text-stone-800',
  'Bodenbeläge': 'bg-orange-100 text-orange-800',
  'Malerarbeiten': 'bg-pink-100 text-pink-800',
  'Bauzeichnungen': 'bg-teal-100 text-teal-800',
};

export default function ProjectsGallery() {
  const [activeCategory, setActiveCategory] = useState('Alle');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  const filtered =
    activeCategory === 'Alle'
      ? projects
      : projects.filter((p) => p.category === activeCategory);

  return (
    <section className="py-12 sm:py-20 bg-[#F8F7F4]">
      <div className="container mx-auto px-4 sm:px-6">

        {/* Filter Bar */}
        <div className="flex flex-wrap gap-2 sm:gap-3 mb-10 sm:mb-14 justify-center">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                activeCategory === cat
                  ? 'bg-[#0D2B4E] text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {cat}
              {cat === 'Alle' && (
                <span className={`ml-1.5 text-xs ${activeCategory === cat ? 'text-white/70' : 'text-gray-400'}`}>
                  ({projects.length})
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Count */}
        <p className="text-sm text-gray-500 mb-6">
          {filtered.length} {filtered.length === 1 ? 'Projekt' : 'Projekte'} gefunden
        </p>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {filtered.map((project) => {
            const badgeClass = categoryColors[project.category] ?? 'bg-gray-100 text-gray-700';
            return (
              <article
                key={project.id}
                onClick={() => setSelectedProject(project)}
                className="group bg-white rounded-xl overflow-hidden cursor-pointer hover:-translate-y-1 transition-all duration-300 border border-gray-100"
              >
                {/* Image */}
                <div className="relative h-52 sm:h-60 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />

                  {/* Overlay zoom icon */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-12 h-12 flex items-center justify-center bg-white/90 rounded-full">
                      <i className="ri-zoom-in-line text-[#0D2B4E] text-xl" />
                    </div>
                  </div>

                  {/* Year badge */}
                  <div className="absolute top-3 right-3">
                    <span className="bg-white/90 text-gray-700 text-xs font-semibold px-2.5 py-1 rounded-full">
                      {project.year}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4 sm:p-5">
                  {/* Category badge */}
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${badgeClass}`}>
                    {project.category}
                  </span>

                  <h3 className="text-base sm:text-lg font-bold text-gray-900 mt-2.5 mb-1.5 leading-snug">
                    {project.title}
                  </h3>

                  <p className="text-sm text-gray-500 line-clamp-2 leading-relaxed">
                    {project.description}
                  </p>

                  {/* Footer meta */}
                  <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100 text-xs text-gray-400">
                    <span className="flex items-center gap-1">
                      <div className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className="ri-map-pin-line" />
                      </div>
                      {project.location}
                    </span>
                    <span className="flex items-center gap-1">
                      <div className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className="ri-time-line" />
                      </div>
                      {project.duration}
                    </span>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-20 text-gray-400">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <i className="ri-folder-open-line text-5xl" />
            </div>
            <p className="text-lg font-medium">Keine Projekte in dieser Kategorie</p>
          </div>
        )}
      </div>

      {/* Modal */}
      <ProjectModal
        project={selectedProject}
        onClose={() => setSelectedProject(null)}
      />
    </section>
  );
}
